/**
 * Products list page.
 *
 * Mock-aware: when `useMockData()` returns true we render from
 * local mock data; otherwise we use InstantDB queries.
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Boxes, Copy, Filter, Package, Pencil, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  ProductForm,
  type ProductFormValues,
  type ProductKind,
  type SelectOption,
} from "../../../components/forms/ProductForm";
import { useHasPermission } from "../../../lib/permissions";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { useAppSettings } from "../../../lib/appSettings";
import {
  mockProducts,
  mockBrands,
  mockCategories,
  mockTaxRates,
  type MockProduct,
  type MockBrand,
  type MockCategory,
} from "../../../lib/catalogMock";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";

export const Route = createFileRoute("/_app/catalog/")({
  component: ProductsPage,
});

/* ─────────────────────────────────────────────────────────────
 * Types — local row shape that maps to the InstantDB doc + mock entry.
 * ───────────────────────────────────────────────────────────── */

type ProductRow = {
  _id: string;
  sku: string;
  displayName: string;
  description: string | null;
  productKind: ProductKind;
  brandId: string | null;
  categoryId: string | null;
  primaryUnitId: string;
  defaultTaxRateId: string | null;
  hsCode: string | null;
  reorderQuantity: number | null;
  barcodePrimary: string | null;
  standardCostCents: bigint;
  isActive: boolean;
  updatedAt: number;
};

type ActiveFilter = "active" | "archived" | "all";

const KIND_BADGE: Record<ProductKind, { label: string; tone: string }> = {
  manufactured: {
    label: "Manufactured",
    tone: "bg-(--accent-soft) text-(--accent) border-(--border-subtle)",
  },
  imported: {
    label: "Imported",
    tone: "bg-(--info-soft) text-(--info) border-(--border-subtle)",
  },
  service: {
    label: "Service",
    tone: "bg-(--warning-soft) text-(--warning) border-(--border-subtle)",
  },
};

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString("en-BD");
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function ProductsPage() {
  const isMock = useMockData();
  const navigate = useNavigate();
  const { units: settingsUnits } = useAppSettings();
  // "Standard cost" is finance-sensitive — hide from sales / warehouse /
  // factory-manager roles per the office-manager demo brief.
  const canSeeCost = useHasPermission("finance.read");

  // Persistent brand / category lists — kept in sync with the settings pages.
  const [brandsList] = usePersistentMockState<MockBrand[]>("brands", mockBrands);
  const [categoryList] = usePersistentMockState<MockCategory[]>("categories", mockCategories);

  // Local mutable copy so create/update in mock mode immediately reflects.
  const [seedRows, setSeedRows] = usePersistentMockState<ProductRow[]>(
    "products",
    isMock ? mockProducts.map(toRow) : [],
  );

  // Live data path — InstantDB queries will replace this once wired.
  // For now mock mode is the dev surface.
  const rows: ProductRow[] = isMock ? seedRows : seedRows;

  // Filters
  const [filterCategory, setFilterCategory] = useState<string | "all">("all");
  const [filterBrand, setFilterBrand] = useState<string | "all">("all");
  const [filterKind, setFilterKind] = useState<ProductKind | "all">("all");
  const [filterActive, setFilterActive] = useState<ActiveFilter>("active");

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      if (filterCategory !== "all" && p.categoryId !== filterCategory)
        return false;
      if (filterBrand !== "all" && p.brandId !== filterBrand) return false;
      if (filterKind !== "all" && p.productKind !== filterKind) return false;
      if (filterActive === "active" && !p.isActive) return false;
      if (filterActive === "archived" && p.isActive) return false;
      return true;
    });
  }, [rows, filterCategory, filterBrand, filterKind, filterActive]);

  // Sheet state
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<ProductRow | null>(null);
  const [editingDetail, setEditingDetail] = useState(false);

  /* Lookup options for forms / filters — read from persistent state so
   * categories/brands created via Settings > Master Data are visible. */
  const brandOptions: SelectOption[] = useMemo(
    () => brandsList.filter((b) => b.isActive).map((b) => ({ id: b._id, label: b.displayName })),
    [brandsList],
  );
  const categoryOptions: SelectOption[] = useMemo(
    () => categoryList.filter((c) => c.isActive).map((c) => ({ id: c._id, label: c.displayName })),
    [categoryList],
  );
  const unitOptions: SelectOption[] = useMemo(
    () => settingsUnits.map((u) => ({ id: u._id, label: `${u.displayName} (${u.code})` })),
    [settingsUnits],
  );
  const taxOptions: SelectOption[] = useMemo(
    () => mockTaxRates.map((t) => ({ id: t._id, label: t.displayName })),
    [],
  );

  const brandsById = useMemo(
    () => new Map(brandOptions.map((b) => [b.id, b.label])),
    [brandOptions],
  );
  const categoriesById = useMemo(
    () => new Map(categoryOptions.map((c) => [c.id, c.label])),
    [categoryOptions],
  );

  /* Submit handlers (mock-only writes for now). */
  function handleCreate(values: ProductFormValues) {
    if (rows.some((r) => r.sku.toLowerCase() === values.sku.toLowerCase())) {
      toast.error(`SKU "${values.sku}" already exists`);
      return;
    }
    const newRow: ProductRow = {
      _id: `prod_${Date.now().toString(36)}`,
      sku: values.sku,
      displayName: values.displayName,
      description: values.description || null,
      productKind: values.productKind,
      brandId: values.brandId,
      categoryId: values.categoryId,
      primaryUnitId: values.primaryUnitId,
      defaultTaxRateId: values.defaultTaxRateId,
      hsCode: values.hsCode || null,
      reorderQuantity: null,
      barcodePrimary: null,
      standardCostCents: values.standardCostCents,
      isActive: values.isActive,
      updatedAt: Date.now(),
    };
    setSeedRows((prev) => [newRow, ...prev]);
    toast.success(`Created ${values.sku}`);
    setCreateOpen(false);
  }

  function handleUpdate(rowId: string, values: ProductFormValues) {
    setSeedRows((prev) =>
      prev.map((r) =>
        r._id === rowId
          ? {
              ...r,
              displayName: values.displayName,
              description: values.description || null,
              productKind: values.productKind,
              brandId: values.brandId,
              categoryId: values.categoryId,
              primaryUnitId: values.primaryUnitId,
              defaultTaxRateId: values.defaultTaxRateId,
              hsCode: values.hsCode || null,
              standardCostCents: values.standardCostCents,
              isActive: values.isActive,
              updatedAt: Date.now(),
            }
          : r,
      ),
    );
    toast.success(`Updated ${values.sku}. Audit trail recorded.`);
    setEditingDetail(false);
  }

  function handleDuplicate(row: ProductRow) {
    const newRow: ProductRow = {
      ...row,
      _id: crypto.randomUUID(),
      sku: `${row.sku}-COPY`,
      displayName: `${row.displayName} (Copy)`,
      updatedAt: Date.now(),
    };
    setSeedRows((prev) => [newRow, ...prev]);
    toast.success("Product duplicated successfully");
    setDetailRow(null);
  }

  /* Columns */
  const columns: ColumnDef<ProductRow>[] = useMemo(
    () => {
      const cols: ColumnDef<ProductRow>[] = [
        {
          accessorKey: "sku",
          header: "SKU",
          cell: ({ row }) => (
            <span className="font-mono text-[12px]">{row.original.sku}</span>
          ),
          meta: { mono: true },
        },
        {
          accessorKey: "displayName",
          header: "Name",
          cell: ({ row }) => (
            <span className="font-medium text-(--text-primary)">
              {row.original.displayName}
            </span>
          ),
        },
        {
          accessorKey: "categoryId",
          header: "Category",
          cell: ({ row }) => (
            <span className="text-(--text-secondary)">
              {row.original.categoryId
                ? categoriesById.get(row.original.categoryId) ?? "—"
                : "—"}
            </span>
          ),
        },
        {
          accessorKey: "brandId",
          header: "Brand",
          cell: ({ row }) => (
            <span className="text-(--text-secondary)">
              {row.original.brandId
                ? brandsById.get(row.original.brandId) ?? "—"
                : "—"}
            </span>
          ),
        },
        {
          accessorKey: "productKind",
          header: "Kind",
          cell: ({ row }) => {
            const k = row.original.productKind;
            const meta = KIND_BADGE[k];
            return (
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
                  meta.tone,
                )}
              >
                {meta.label}
              </span>
            );
          },
        },
      ];

      // Cost column is finance-only — hidden from sales / warehouse / factory.
      if (canSeeCost) {
        cols.push({
          id: "cost",
          header: "Standard cost",
          cell: ({ row }) => (
            <span className="font-mono">
              {formatMoney(row.original.standardCostCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ),
          meta: { numeric: true },
        });
      }

      cols.push(
        {
          id: "sellable",
          header: "Status",
          cell: ({ row }) => (
            <StatusPill variant={row.original.isActive ? "success" : "neutral"}>
              {row.original.isActive ? "Active" : "Archived"}
            </StatusPill>
          ),
        },
        {
          accessorKey: "updatedAt",
          header: "Updated",
          cell: ({ row }) => (
            <span className="text-[12px] text-(--text-tertiary)">
              {relativeTime(row.original.updatedAt)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
      );

      return cols;
    },
    [brandsById, categoriesById, canSeeCost],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<ProductRow>[] = [
      { key: "sku", label: "SKU" },
      { key: "displayName", label: "Name" },
      { key: "description", label: "Description", format: (v) => (v == null ? "" : String(v)) },
      { key: "productKind", label: "Kind" },
      {
        key: "categoryId",
        label: "Category",
        value: (r) => (r.categoryId ? categoriesById.get(r.categoryId) ?? "" : ""),
      },
      {
        key: "brandId",
        label: "Brand",
        value: (r) => (r.brandId ? brandsById.get(r.brandId) ?? "" : ""),
      },
      {
        key: "primaryUnitId",
        label: "Unit",
        value: (r) =>
          settingsUnits.find((u) => u._id === r.primaryUnitId)?.code ?? "",
      },
      { key: "hsCode", label: "HS code", format: (v) => (v == null ? "" : String(v)) },
      { key: "barcodePrimary", label: "Barcode", format: (v) => (v == null ? "" : String(v)) },
      ...(canSeeCost
        ? ([{ key: "standardCostCents", label: "Standard cost BDT" }] as ExportColumn<ProductRow>[])
        : []),
      {
        key: "reorderQuantity",
        label: "Reorder qty",
        format: (v) => (v == null ? "" : String(v)),
      },
      { key: "isActive", label: "Active" },
      { key: "updatedAt", label: "Updated", format: (v) => formatDateForCsv(Number(v)) },
    ];
    downloadCsv(
      exportFilename("products", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Products"
        description="Manage SKUs across manufactured, imported, and service categories."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add product
            </Button>
          </>
        }
      />

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        <FilterChip
          label="Category"
          value={filterCategory}
          options={[
            { value: "all", label: "All" },
            ...categoryOptions.map((c) => ({ value: c.id, label: c.label })),
          ]}
          onChange={setFilterCategory}
        />
        <FilterChip
          label="Brand"
          value={filterBrand}
          options={[
            { value: "all", label: "All" },
            ...brandOptions.map((b) => ({ value: b.id, label: b.label })),
          ]}
          onChange={setFilterBrand}
        />
        <FilterChip
          label="Kind"
          value={filterKind}
          options={[
            { value: "all", label: "All" },
            { value: "manufactured", label: "Manufactured" },
            { value: "imported", label: "Imported" },
            { value: "service", label: "Service" },
          ]}
          onChange={(v) => setFilterKind(v as ProductKind | "all")}
        />
        <div
          role="tablist"
          aria-label="Active filter"
          className="ml-auto inline-flex rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-0.5"
        >
          {(["active", "archived", "all"] as ActiveFilter[]).map((opt) => (
            <button
              key={opt}
              type="button"
              role="tab"
              aria-selected={filterActive === opt}
              onClick={() => setFilterActive(opt)}
              className={cn(
                "px-2.5 py-1 text-[12px] font-medium rounded transition-colors capitalize",
                filterActive === opt
                  ? "bg-(--bg-surface) text-(--text-primary) shadow-[var(--shadow-xs)]"
                  : "text-(--text-secondary) hover:text-(--text-primary)",
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No products match"
        emptyDescription={
          rows.length === 0
            ? "Add your first SKU to get started."
            : "Try widening your filters."
        }
        emptyIcon={<Package className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add product
            </Button>
          ) : undefined
        }
      />

      {/* Create Sheet */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Add product</SheetTitle>
            <SheetDescription>
              Define a master SKU for your product catalog.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <ProductForm
              brands={brandOptions}
              categories={categoryOptions}
              units={unitOptions}
              taxRates={taxOptions}
              submitLabel="Create product"
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingDetail(false);
          }
        }}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 flex-wrap">
                  <span>{detailRow.displayName}</span>
                  <Badge variant="neutral" className="font-mono">
                    {detailRow.sku}
                  </Badge>
                  <RecordAuditBadge
                    tableName="products"
                    recordId={detailRow._id}
                  />
                </SheetTitle>
                <SheetDescription>
                  {KIND_BADGE[detailRow.productKind].label} ·{" "}
                  {detailRow.isActive ? "Active" : "Archived"}
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                {editingDetail ? (
                  <ProductForm
                    brands={brandOptions}
                    categories={categoryOptions}
                    units={unitOptions}
                    taxRates={taxOptions}
                    skuLocked
                    submitLabel="Save changes"
                    initialValues={{
                      sku: detailRow.sku,
                      displayName: detailRow.displayName,
                      description: detailRow.description ?? "",
                      productKind: detailRow.productKind,
                      brandId: detailRow.brandId,
                      categoryId: detailRow.categoryId,
                      primaryUnitId: detailRow.primaryUnitId,
                      defaultTaxRateId: detailRow.defaultTaxRateId,
                      hsCode: detailRow.hsCode ?? "",
                      standardCostCents: detailRow.standardCostCents,
                      isActive: detailRow.isActive,
                      isSellable: detailRow.isActive,
                      isPurchasable: detailRow.productKind !== "service",
                    }}
                    onSubmit={(v) => handleUpdate(detailRow._id, v)}
                    onCancel={() => setEditingDetail(false)}
                  />
                ) : (
                  <Tabs defaultValue="overview">
                    <TabsList>
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="history">History</TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview" className="mt-4">
                      <ProductDetailReadonly
                        row={detailRow}
                        brandLabel={
                          detailRow.brandId
                            ? brandsById.get(detailRow.brandId)
                            : null
                        }
                        categoryLabel={
                          detailRow.categoryId
                            ? categoriesById.get(detailRow.categoryId)
                            : null
                        }
                        onEdit={() => setEditingDetail(true)}
                        onOpenFull={() => {
                          setDetailRow(null);
                          navigate({
                            to: "/catalog/products/$productId",
                            params: { productId: detailRow._id },
                          });
                        }}
                      />
                    </TabsContent>
                    <TabsContent value="history" className="mt-4">
                      <RecordAuditTrail
                        tableName="products"
                        recordId={detailRow._id}
                      />
                    </TabsContent>
                  </Tabs>
                )}
              </SheetBody>
              {!editingDetail ? (
                <SheetFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDetailRow(null);
                      navigate({
                        to: "/catalog/products/$productId",
                        params: { productId: detailRow._id },
                      });
                    }}
                  >
                    Open full page
                  </Button>
                  <Button variant="outline" onClick={() => handleDuplicate(detailRow)}>
                    <Copy className="h-4 w-4" /> Duplicate
                  </Button>
                  <Button size="icon" onClick={() => setEditingDetail(true)} aria-label="Edit">
                    <Pencil className="size-4" />
                  </Button>
                </SheetFooter>
              ) : null}
            </>
          ) : (
            <EmptyState title="No product selected" />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function toRow(p: MockProduct): ProductRow {
  return {
    _id: p._id,
    sku: p.sku,
    displayName: p.displayName,
    description: p.description,
    productKind: p.productKind,
    brandId: p.brandId,
    categoryId: p.categoryId,
    primaryUnitId: p.primaryUnitId,
    defaultTaxRateId: p.defaultTaxRateId,
    hsCode: p.hsCode,
    reorderQuantity: p.reorderQuantity,
    barcodePrimary: p.barcodePrimary,
    standardCostCents: p.standardCostCents,
    isActive: p.isActive,
    updatedAt: p.updatedAt,
  };
}

/* ─────────────────────────────────────────────────────────────
 * Subcomponents
 * ───────────────────────────────────────────────────────────── */

function FilterChip<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="h-6 bg-transparent pr-1 text-[12px] text-(--text-primary) focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProductDetailReadonly({
  row,
  brandLabel,
  categoryLabel,
  onEdit,
  onOpenFull,
}: {
  row: ProductRow;
  brandLabel?: string | null;
  categoryLabel?: string | null;
  onEdit: () => void;
  onOpenFull: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3">
        <DetailItem label="SKU" mono>
          {row.sku}
        </DetailItem>
        <DetailItem label="Kind">{KIND_BADGE[row.productKind].label}</DetailItem>
        <DetailItem label="Brand">{brandLabel ?? "—"}</DetailItem>
        <DetailItem label="Category">{categoryLabel ?? "—"}</DetailItem>
        <DetailItem label="HS Code" mono>
          {row.hsCode ?? "—"}
        </DetailItem>
        <DetailItem label="Reorder Qty">
          {row.reorderQuantity != null ? row.reorderQuantity : "—"}
        </DetailItem>
        <DetailItem label="Barcode" mono>
          {row.barcodePrimary ?? "—"}
        </DetailItem>
        <DetailItem label="Updated">{relativeTime(row.updatedAt)}</DetailItem>
      </div>

      {row.description ? (
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) mb-1">
            Description
          </div>
          <p className="text-[13px] text-(--text-primary) whitespace-pre-wrap">
            {row.description}
          </p>
        </div>
      ) : null}

      <div className="rounded-md border border-dashed border-(--border-subtle) p-3 flex items-center gap-3">
        <Boxes className="h-4 w-4 text-(--text-tertiary)" />
        <div className="text-[12px] text-(--text-secondary) flex-1">
          For BOM, pricing and stock — open the full product page.
        </div>
        <Button variant="outline" size="sm" onClick={onOpenFull}>
          Open
        </Button>
        <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span
        className={cn(
          "text-[13px] text-(--text-primary)",
          mono && "font-mono",
        )}
      >
        {children}
      </span>
    </div>
  );
}
